// ==========================================================
// Babilonia + NR / Prodesk (Originación) - Módulo de servicio
// ----------------------------------------------------------
// Mismo patrón que NR_ENDPOINTS/apiPost en crearventa.js (Procotiza),
// pero para el flujo de Originación: Token -> Catálogos -> Propuesta
// -> Firma NAAT (ver "Documentación API Prodesk" de ubimia).
//
// Este archivo NO pega directo a NR: pega a tu propia API
// (financiamiento/prodesk/...), que es la que ya tiene el token,
// hace el proxy a https://uat.losnrtelepro.com.mx/prodesk_api y
// guarda el log (prodesk_function.php / prodesk.php en el backend).
//
// Uso típico dentro de un componente:
//   import { useProdesk } from "./prodeskApi";
//   const prodesk = useProdesk();
//   const sexo = await prodesk.consultarCatalogo({ isCatalog: 1, valueFind: 25 });
// ==========================================================

import { useCallback, useState } from "react";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://api.checkmark.mx/";

// ----------------------------------------------------------
// Endpoints internos (Babilonia) que envuelven a Prodesk.
// ----------------------------------------------------------
export const PRODESK_ENDPOINTS = {
  token: "financiamiento/prodesk/token",
  catalogo: "financiamiento/prodesk/catalogo",
  catalogoCodigoPostal: "financiamiento/prodesk/catalogo/codigo-postal",
  propuesta: "financiamiento/prodesk/propuesta",
  firmaNaat: "financiamiento/prodesk/firma-naat",
};

// Identificadores fijos de catálogo general (sección 7 de la
// documentación de ubimia). IsCatalog=1, ValueFind=<id>.
export const PRODESK_CATALOGOS_GENERALES = {
  sexo: 25,
  paisNacimiento: 23,
  estadoCivil: 28,
  nacionalidad: 26,
  ciudadNacimiento: 24,
  actividadEmpresa: 83,
  descripcionEmpresa: 82, // requiere Campo = id de actividadEmpresa seleccionado
};

// Valores fijos documentados por ubimia (no son catálogo, van tal cual).
export const PRODESK_CREDIT_TYPE = {
  key: 1468,
  value: "",
  description: "N/A",
};

export const PRODESK_ADDRESS_TYPE = {
  key: 0,
  value: "",
  description: "Domicilio Personal",
};

export const PRODESK_TIPO_VIVIENDA = {
  PROPIA: { key: 1468, description: "Propia" },
  RENTADA: { key: 1469, description: "Rentada" },
};

// ----------------------------------------------------------
// Helper HTTP - mismo contrato que apiPost en crearventa.js:
// tu API regresa { statusCode, message, data }, aquí se
// desenvuelve "data" y se lanza Error si hubo problema.
// ----------------------------------------------------------
async function apiPost(endpoint, payload = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });

  let json = {};
  try {
    json = await response.json();
  } catch (error) {
    throw new Error("La API respondió sin JSON válido.");
  }

  const responseData = json?.data ?? json;
  const statusCode = Number(json?.statusCode || response.status || 0);
  const errorLike = !response.ok || statusCode >= 400;

  if (errorLike) {
    const apiError = new Error(
      json?.message || responseData?.message || `Error consultando ${endpoint}`,
    );
    apiError.status = statusCode;
    apiError.payload = responseData;
    throw apiError;
  }

  return responseData;
}

// ----------------------------------------------------------
// Funciones de servicio (uso directo, sin hook, si lo prefieres
// fuera de un componente — por ejemplo en un thunk o util).
// ----------------------------------------------------------

// Solo hace falta si quieres precachear el token tú mismo; los demás
// endpoints ya lo obtienen solos en el backend si no se los mandas.
export function prodeskObtenerToken({ ventaId, usuarioProdesk, passwordProdesk } = {}) {
  return apiPost(PRODESK_ENDPOINTS.token, {
    venta_id: ventaId,
    usuario_prodesk: usuarioProdesk,
    password_prodesk: passwordProdesk,
  });
}

// isCatalog=1 + valueFind=<id catálogo general> (ver PRODESK_CATALOGOS_GENERALES)
// isCatalog=0 + valueFind=<código postal> + campo=COLONIA|MUNICIPIO|ESTADO|CIUDAD
export function prodeskConsultarCatalogo({ isCatalog, valueFind, campo = "", ventaId, token } = {}) {
  return apiPost(PRODESK_ENDPOINTS.catalogo, {
    is_catalog: isCatalog,
    value_find: valueFind,
    campo,
    venta_id: ventaId,
    token,
  });
}

// Atajo: consulta COLONIA, MUNICIPIO, ESTADO y CIUDAD de un CP en una
// sola llamada (o solo uno si mandas `campo`).
export function prodeskConsultarCodigoPostal({ zipCode, campo, ventaId, token } = {}) {
  return apiPost(PRODESK_ENDPOINTS.catalogoCodigoPostal, {
    zip_code: zipCode,
    campo,
    venta_id: ventaId,
    token,
  });
}

// `propuesta` debe traer la forma documentada: { creditType,
// participants: [...], folioCotizacionPortal }. Usa los builders de
// abajo (buildProdeskParticipant / buildProdeskAddress) para armarla.
export function prodeskGuardarPropuesta({ propuesta, ventaId, token } = {}) {
  return apiPost(PRODESK_ENDPOINTS.propuesta, {
    propuesta,
    venta_id: ventaId,
    token,
  });
}

export function prodeskGenerarFirmaNaat({ folio, ventaId, token } = {}) {
  return apiPost(PRODESK_ENDPOINTS.firmaNaat, {
    folio,
    venta_id: ventaId,
    token,
  });
}

// ----------------------------------------------------------
// Builders — arman los sub-objetos con la forma exacta que pide
// Proposal.svc/json (sección 5 de la documentación), a partir de
// datos "planos" capturados en el formulario.
// ----------------------------------------------------------

// catalogItem = { key, value, description } tal como lo regresa
// prodeskConsultarCatalogo (catalogItems[i]).
export function toProdeskCatalogObject(catalogItem, fallbackDescription = "") {
  if (!catalogItem) {
    return { key: 0, value: "", description: fallbackDescription };
  }
  return {
    key: catalogItem.key ?? 0,
    value: catalogItem.value ?? "",
    description: catalogItem.description ?? fallbackDescription,
  };
}

export function buildProdeskAddress({
  addressAntiquity,
  city,
  tipovivienda, // "PROPIA" | "RENTADA"
  extNumber,
  intNumber,
  municipality,
  neighborhoodCatalogItem, // catalogItem de Colonia por CP
  state,
  street,
  zipCode,
}) {
  return {
    addressAntiquity: Number(addressAntiquity) || 0,
    city,
    addressType: { ...PRODESK_ADDRESS_TYPE },
    tipovivienda: PRODESK_TIPO_VIVIENDA[tipovivienda]?.description?.toUpperCase() || tipovivienda,
    extNumber,
    intNumber,
    municipality,
    neighborhood: toProdeskCatalogObject(neighborhoodCatalogItem, "Descripcion colonia"),
    state,
    street,
    zipCode,
  };
}

export function buildProdeskEmployment({
  admissionDateMs, // milisegundos desde Unix (Date.now() o +new Date(...))
  currentEmployment = true,
  economicActivity,
  descripcionEmpleo,
  ingresoFijo,
  ingresoVariable,
  nombreEmpresa,
  asalariadoPeriodicidad, // "MENSUAL" | "QUINCENAL" | "SEMANAL"
  tipoIngreso, // "ASALARIADO" | "INDEPENDIENTE"
}) {
  return {
    admissionDate: `/Date(${Number(admissionDateMs) || 0})/`,
    currentEmployment: Boolean(currentEmployment),
    economicActivity,
    DescripcionEmpleo: descripcionEmpleo,
    ingresofijo: String(ingresoFijo ?? ""),
    ingresovariable: String(ingresoVariable ?? ""),
    nombreempresa: nombreEmpresa,
    AsalariadoPeriodicidad: asalariadoPeriodicidad,
    TipoIngreso: tipoIngreso,
  };
}

export function buildProdeskCreditsBureau({
  mortgageCredit = "0",
  tc = "0",
  authorization = "0",
  automotiveCredit = "0",
  tcDigits = "",
  aceptacionPrivacidad = "0",
}) {
  return {
    MortgageCredit: String(mortgageCredit),
    TC: String(tc),
    Authorization: String(authorization),
    AutomotiveCredit: String(automotiveCredit),
    TCDigits: String(tcDigits),
    AceptacionPrivacidad: String(aceptacionPrivacidad),
  };
}

export function buildProdeskParticipant({
  curp,
  rfc,
  firstName,
  secondName = "",
  surname,
  secondSurname = "",
  birthDateMs, // milisegundos desde Unix
  cellPhone,
  email,
  genderCatalogItem,
  maritalStatusCatalogItem,
  nationalityCatalogItem,
  nativeCity,
  nativeCountry,
  addresses = [],
  employments = [],
  creditsBureau = [],
}) {
  return {
    CURP: curp,
    RFC: rfc,
    firstName,
    secondName,
    surname,
    secondSurname,
    birthDate: `/Date(${Number(birthDateMs) || 0})/`,
    cellPhone,
    email,
    participantType: 0,
    gender: toProdeskCatalogObject(genderCatalogItem, "Genero de la persona"),
    maritalStatus: toProdeskCatalogObject(maritalStatusCatalogItem, "Estado civil de la persona"),
    nationality: toProdeskCatalogObject(nationalityCatalogItem, "Nacionalidad de la persona"),
    nativeCity,
    nativeCountry,
    addresses,
    employments,
    CreditsBureau: creditsBureau,
  };
}

export function buildProdeskPropuesta({ participants, folioCotizacionPortal }) {
  return {
    creditType: { ...PRODESK_CREDIT_TYPE },
    participants,
    folioCotizacionPortal,
  };
}

// ----------------------------------------------------------
// Hook de conveniencia para componentes React: mismo espíritu que
// apiPostSafe/loading en crearventa.js, con loading/error por acción.
// ----------------------------------------------------------
export function useProdesk() {
  const [loading, setLoading] = useState({});
  const [lastError, setLastError] = useState("");

  const runAction = useCallback(async (key, fn) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    setLastError("");
    try {
      return await fn();
    } catch (error) {
      setLastError(error?.message || String(error));
      throw error;
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  const obtenerToken = useCallback(
    (params) => runAction("token", () => prodeskObtenerToken(params)),
    [runAction],
  );

  const consultarCatalogo = useCallback(
    (params) => runAction("catalogo", () => prodeskConsultarCatalogo(params)),
    [runAction],
  );

  const consultarCodigoPostal = useCallback(
    (params) => runAction("codigoPostal", () => prodeskConsultarCodigoPostal(params)),
    [runAction],
  );

  const guardarPropuesta = useCallback(
    (params) => runAction("propuesta", () => prodeskGuardarPropuesta(params)),
    [runAction],
  );

  const generarFirmaNaat = useCallback(
    (params) => runAction("firmaNaat", () => prodeskGenerarFirmaNaat(params)),
    [runAction],
  );

  return {
    loading,
    lastError,
    obtenerToken,
    consultarCatalogo,
    consultarCodigoPostal,
    guardarPropuesta,
    generarFirmaNaat,
  };
}